/**
 * Card network marks, drawn rather than fetched.
 *
 * These are card *networks* (Visa, Mastercard…), not the payment provider — so
 * they do not violate HC-2. A customer expects to see their card's network
 * recognised; it is the single strongest signal that a checkout is working
 * properly, and every real payment page shows it.
 */
const BRANDS = {
  visa: (
    <g>
      <rect width="32" height="21" rx="3" fill="#1A1F71" />
      <path d="M13.2 14.6h-1.9l1.2-7.2h1.9l-1.2 7.2zM10 7.4l-1.8 5-.2-1.1-.6-3.3s-.1-.6-.8-.6H3.7l0 .1s.8.2 1.8.8l1.7 6.3h2l3-7.2H10zM24.6 14.6h1.7l-1.5-7.2h-1.5c-.7 0-.9.5-.9.5l-2.8 6.7h2l.4-1.1h2.4l.2 1.1zm-2.1-2.6l1-2.7.6 2.7h-1.6zM19.4 9.2l.3-1.6s-.8-.3-1.7-.3c-1 0-3.3.4-3.3 2.4 0 1.9 2.6 1.9 2.6 2.9s-2.3.8-3.1.2l-.3 1.7s.8.4 2.1.4c1.3 0 3.3-.7 3.3-2.5 0-1.9-2.7-2-2.7-2.9s1.8-.8 2.8-.3z" fill="#fff" />
    </g>
  ),
  mastercard: (
    <g>
      <rect width="32" height="21" rx="3" fill="#16181C" />
      <circle cx="12.8" cy="10.5" r="5.6" fill="#EB001B" />
      <circle cx="19.2" cy="10.5" r="5.6" fill="#F79E1B" />
      <path d="M16 6.2a5.6 5.6 0 000 8.6 5.6 5.6 0 000-8.6z" fill="#FF5F00" />
    </g>
  ),
  amex: (
    <g>
      <rect width="32" height="21" rx="3" fill="#006FCF" />
      <path d="M6 8.4h3.1l.5 1.2.5-1.2h8.3v.8l.5-.8h3l.5 1.1.5-1.1H26v6H6v-6zm1.2 1.3l-1.5 3.4h1l.3-.7h1.6l.3.7h1l-1.5-3.4H7.2zm.7.9l.5 1.1h-1l.5-1.1zm3.3-.9v3.4h.9v-2.4l1 2.4h.8l1-2.4v2.4h.9V9.7h-1.5l-.8 2-.8-2h-1.5zm5.6 0v3.4h2.9v-.8h-2v-.6h1.9v-.7h-1.9v-.6h2v-.7h-2.9zm3.6 0v3.4h.9v-1.2h.5l1 1.2h1.1l-1.1-1.3c.5-.1.9-.5.9-1 0-.7-.5-1.1-1.3-1.1h-2zm.9.7h1c.3 0 .5.2.5.4s-.2.4-.5.4h-1v-.8z" fill="#fff" />
    </g>
  ),
  discover: (
    <g>
      <rect width="32" height="21" rx="3" fill="#F7F7F7" stroke="#E2E2DB" />
      <path d="M32 13.5V18a3 3 0 01-3 3H14c6-1.5 13-3.9 18-7.5z" fill="#F76E11" />
      <text x="4" y="13" fontFamily="Inter,sans-serif" fontSize="6.5" fontWeight="700" fill="#16181C">DISC</text>
    </g>
  ),
  jcb: (
    <g>
      <rect width="32" height="21" rx="3" fill="#fff" stroke="#E2E2DB" />
      <rect x="4" y="5" width="7" height="11" rx="2" fill="#0E4C96" />
      <rect x="12.5" y="5" width="7" height="11" rx="2" fill="#D42D3C" />
      <rect x="21" y="5" width="7" height="11" rx="2" fill="#00A24A" />
    </g>
  ),
  diners: (
    <g>
      <rect width="32" height="21" rx="3" fill="#0079BE" />
      <circle cx="16" cy="10.5" r="6.5" fill="#fff" />
      <path d="M16 5.5a5 5 0 000 10 5 5 0 000-10zm-1 8.2a3.5 3.5 0 010-6.4v6.4zm2 0V7.3a3.5 3.5 0 010 6.4z" fill="#0079BE" />
    </g>
  ),
};

/** A neutral placeholder before enough digits are typed to know the network. */
const GENERIC = (
  <g opacity="0.45">
    <rect x="0.5" y="0.5" width="31" height="20" rx="3" fill="none" stroke="rgb(var(--slate-300))" />
    <rect x="0.5" y="5.5" width="31" height="3.5" fill="rgb(var(--slate-300))" />
    <rect x="4" y="13" width="7" height="2" rx="1" fill="rgb(var(--slate-300))" />
  </g>
);

/**
 * Monochrome wordmarks, for use on the card preview itself.
 *
 * The full-colour tiles are unreadable there — Visa's mark is navy, and it sits
 * on a navy card. Every real card prints its network in white for exactly this
 * reason, so the `mono` variant does the same.
 */
const MONO = {
  visa: <text x="16" y="15" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="11" fontWeight="700" fontStyle="italic" letterSpacing="0.5" fill="currentColor">VISA</text>,
  mastercard: (
    <g fill="currentColor">
      <circle cx="12.5" cy="10.5" r="6.4" opacity="0.75" />
      <circle cx="19.5" cy="10.5" r="6.4" opacity="0.75" />
    </g>
  ),
  amex: <text x="16" y="14" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="7.2" fontWeight="700" letterSpacing="0.3" fill="currentColor">AMEX</text>,
  discover: <text x="16" y="14" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="6.4" fontWeight="700" letterSpacing="0.2" fill="currentColor">DISCOVER</text>,
  jcb: <text x="16" y="14.5" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="8.5" fontWeight="700" letterSpacing="0.4" fill="currentColor">JCB</text>,
  diners: <text x="16" y="14" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="6" fontWeight="700" letterSpacing="0.2" fill="currentColor">DINERS</text>,
};

export function CardBrandIcon({ brand, variant = 'colour', className = 'h-[21px] w-8' }) {
  if (variant === 'mono') {
    return (
      <svg viewBox="0 0 32 21" className={className} role="img" aria-label={brand ? `${brand} card` : 'Card'}>
        {MONO[brand] ?? (
          <text x="16" y="14" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="6.5"
            fontWeight="600" letterSpacing="1.2" fill="currentColor" opacity="0.5">CARD</text>
        )}
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 32 21" className={className} role="img" aria-label={brand ? `${brand} card` : 'Card'}>
      {BRANDS[brand] ?? GENERIC}
    </svg>
  );
}

export default CardBrandIcon;
